<template>
  <!--  show PWA-->
  <!--  <PWABlock v-if="true" />-->
  <SomethingGoingWrong
    v-if="store.state.app.isCrashed"
  />
  <PaymentReturnToTelegram
    v-else-if="showPaymentReturnToTelegram"
  />
  <PWABlock
    v-else-if="!state.isStandalone && !route.meta.isPublic"
  />
  <NewVersionAvailable
    v-else-if="!store.state.user.isRestoring && (store.state.app.isNewVersionAvailable || store.state.app.isVersionRefreshing)"
  />
  <NoConnection
    v-else-if="!store.state.app.isOnline"
  />
  <router-view
    v-else-if="route.meta.isDesktop && !store.state.user.isRestoring"
  />
  <router-view
    v-else-if="!store.state.user.isRestoring"
    v-slot="{ Component, route: viewRoute }"
  >
    <Transition
      :name="routeTransition || 'route-none'"
      :css="Boolean(routeTransition)"
      @before-leave="freezeLeavingPage"
    >
      <component
        :is="Component"
        :key="viewRoute.fullPath"
      />
    </Transition>
  </router-view>
  <transition
    v-show="store.state.user.isRestoring"
    name="fade"
  >
    <SplashScreen
      v-if="state.splashScreenNeeded"
    />
  </transition>

  <component
    :is="messageComponents[store.state.app.messageComponent]"
    v-if="store.state.app.messageComponent"
    :data="store.state.app.messageComponentData"
  />

  <ViewportDebugOverlay />
</template>

<script setup>
import {computed, defineAsyncComponent, onBeforeMount, onUnmounted, reactive, ref, watch} from 'vue'
import {useRoute} from 'vue-router'
import store from './store/store'
import SplashScreen from '@/views/SplashScreen.vue'
import {imagesLinkCache} from '@/utils/imagesLinkCache'
import NoConnection from '@/views/system/NoConnection.vue'
import SomethingGoingWrong from '@/views/system/SomethingGoingWrong.vue'
import {pwaUtils} from "@/utils/pwaUtils.js"
import NewVersionAvailable from '@/views/system/NewVersionAvailable.vue'
import {yandexMetrika} from "@/external/yandexWebisor.js"
import {messageComponents} from '@/components/messageComponents/messageComponents.js'
import {appType, appTypes} from '@/appType/appType.js'
import ViewportDebugOverlay from '@/components/debug/ViewportDebugOverlay.vue'
import {startDocumentHeightSync} from '@/utils/syncDocumentHeight.js'
import {startTelegramViewportHeightSync} from '@/utils/syncTelegramViewportHeight.js'
import {
  bindExternalPaymentReturnListeners,
  restorePendingExternalPaymentOnBoot,
} from '@/composables/useExternalPaymentReturn.js'
import {resolveFlowTransition} from '@/router/flowTransition.js'

const PWABlock = defineAsyncComponent(() => import('@/views/system/PWABlock.vue'))
const PaymentReturnToTelegram = defineAsyncComponent(() => import('@/views/system/PaymentReturnToTelegram.vue'))

const route = useRoute()

const routeTransition = ref(null)

watch(() => route.name, (toName, fromName) => {
  routeTransition.value = resolveFlowTransition(toName, fromName)
})

/**
 * Уходящая страница фиксируется там, где её видел пользователь. В Telegram Mini App
 * прокручивается сам документ (html.app-telegram), и без этого прокрученная страница
 * прыгала бы к верху ещё до начала анимации. Высоту не трогаем — её задают стили страницы.
 */
function freezeLeavingPage(el) {
  if (!routeTransition.value) {
    return
  }

  const rect = el.getBoundingClientRect()
  el.style.top = `${rect.top}px`
  el.style.left = `${rect.left}px`
  el.style.width = `${rect.width}px`
}

const state = reactive({
  splashScreenNeeded: true,
  isStandalone: pwaUtils.isStandalone(),
  checkInterval: null,
  scrollTop: document.documentElement.scrollTop,
})

/** Банк вернул в Safari/Chrome на /?redirect_action=payment_result — не QR, а «вернись в Telegram» */
const showPaymentReturnToTelegram = computed(() => (
  store.getters['app/isBodybaseProject']
  && appType !== appTypes.TELEGRAM
  && !state.isStandalone
  && route.query.redirect_action === 'payment_result'
))

let stopDocumentHeightSync = null
let stopTelegramViewportSync = null
let stopExternalPaymentListeners = null

watch(() => store.state.user.isRestoring, isRestoring => {
  if (!isRestoring) {
    // Theme CSS vars (button colors etc.) are applied in app/init during restore.
    // Keep the index.html boot-loader until then to avoid unstyled NewVersionAvailable FOUC.
    const boot = document.getElementById('boot-loader')
    if (boot) {
      boot.hidden = true
      boot.remove()
    }

    setTimeout(() => {
      state.splashScreenNeeded = false
    }, 500)

    if (store.getters['app/isBodybaseProject'] && appType === appTypes.TELEGRAM) {
      void restorePendingExternalPaymentOnBoot()
    }
  } else {
    state.splashScreenNeeded = true
  }
}, { immediate: true })

onBeforeMount(async () => {
  imagesLinkCache.restore()
  document.body.classList.add('dark-theme')

  if (state.isStandalone) {
    yandexMetrika('reachGoal', 'appInstalled')
  }

  // PWA only: restore height after external payment return (stuck small svh).
  // Never on Telegram Mini App — isStandalone() is true for TMA, but screen.height
  // is the monitor while innerHeight is the iframe; collapse heuristic then sets
  // --document-height ≈ screen (e.g. 1393px in a 590px iframe) and clips scroll/nav.
  if (state.isStandalone && appType !== appTypes.TELEGRAM) {
    stopDocumentHeightSync = startDocumentHeightSync()
  } else if (appType === appTypes.TELEGRAM) {
    const root = document.documentElement
    root.classList.add('app-telegram')
    root.style.removeProperty('--document-height')
    root.style.removeProperty('--document-offset-top')
    root.style.removeProperty('--document-offset-bottom')
    // Viewport CSS vars for bar pin; shell height is not locked (html.app-telegram).
    stopTelegramViewportSync = startTelegramViewportHeightSync()

    if (store.getters['app/isBodybaseProject']) {
      stopExternalPaymentListeners = bindExternalPaymentReturnListeners()
    }
  }
})

onUnmounted(() => {
  clearInterval(state.checkInterval)
  stopDocumentHeightSync?.()
  stopTelegramViewportSync?.()
  stopExternalPaymentListeners?.()
})

</script>

<style lang="scss">
@import "@/assets/variables";

:root {
  // Fallback until startTelegramViewportHeightSync sets a px value (bar / consumers).
  --document-height: calc(100svh - 0px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease, background-color .3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  background-color: transparent;
}

// Переходы между экранами воронки оплаты (см. router/flowTransition.js).
// Новый экран выезжает на 24px и проявляется, старый уходит в другую сторону и гаснет.
.route-forward-enter-active,
.route-back-enter-active {
  transition:
    opacity 300ms cubic-bezier(0.215, 0.61, 0.355, 1) 60ms,
    transform 380ms cubic-bezier(0.16, 1, 0.3, 1) 60ms;
}

.route-forward-leave-active,
.route-back-leave-active {
  // Координаты проставляет freezeLeavingPage
  position: fixed;
  pointer-events: none;
  transition:
    opacity 180ms cubic-bezier(0.55, 0.055, 0.675, 0.19),
    transform 220ms cubic-bezier(0.55, 0.055, 0.675, 0.19);
}

.route-forward-enter-from,
.route-back-leave-to {
  opacity: 0;
  transform: translateX(24px);
}

.route-forward-leave-to,
.route-back-enter-from {
  opacity: 0;
  transform: translateX(-24px);
}

@media (prefers-reduced-motion: reduce) {
  .route-forward-enter-from,
  .route-forward-leave-to,
  .route-back-enter-from,
  .route-back-leave-to {
    transform: none;
  }
}
</style>
